import {
  addDoc,
  collection,
  getDocs,
  getCountFromServer,
  limit,
  orderBy,
  query,
  serverTimestamp,
  startAfter,
  Timestamp,
  type QueryConstraint,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '@/lib/firebase';
import { MAX_TRIP_MEMORY_SIZE_BYTES, TRIP_MEMORY_ALLOWED_MIME_TYPES } from './uploadPolicy';

export type TripMemoryVisibility = 'owner' | 'collaborators';

export type TripMemory = {
  id: string;
  tripId: string;
  createdBy: string;
  text: string;
  photoUrl?: string | null;
  visibility: TripMemoryVisibility;
  createdAt: Date;
};

export type TripMemoryInput = {
  text: string;
  photoUri?: string | null;
  visibility?: TripMemoryVisibility;
};

export type TripMemoryPreview = {
  count: number;
  latestSnippet?: string;
  photoUrl?: string | null;
  lastContributor?: string;
};

const sanitizeSnippet = (value?: string): string | undefined => {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return trimmed.length > 96 ? `${trimmed.slice(0, 93)}…` : trimmed;
};

const uploadMemoryPhoto = async (tripId: string, userId: string, uri: string): Promise<string> => {
  const response = await fetch(uri);
  if (!response.ok) {
    throw new Error('Unable to read selected media');
  }
  const blob = await response.blob();
  if (blob.size > MAX_TRIP_MEMORY_SIZE_BYTES) {
    throw new Error('Photo exceeds 10MB limit');
  }
  const normalizedMime = (blob.type || 'image/jpeg').toLowerCase();
  if (!TRIP_MEMORY_ALLOWED_MIME_TYPES.has(normalizedMime)) {
    throw new Error('Unsupported photo type. Use JPG, PNG, HEIC, or HEIF');
  }
  const extension = blob.type.includes('png') ? 'png' : 'jpg';
  const storagePath = `tripMemories/${tripId}/${userId}-${Date.now()}.${extension}`;
  const storageRef = ref(storage, storagePath);
  const metadata = {
    contentType: normalizedMime,
    customMetadata: {
      tripId,
      uploadedBy: userId,
    },
  };
  await uploadBytes(storageRef, blob, metadata);
  return getDownloadURL(storageRef);
};

export const addTripMemory = async (
  tripId: string,
  userId: string,
  input: TripMemoryInput,
): Promise<void> => {
  if (!tripId || !userId) {
    throw new Error('tripId and userId are required to add a memory');
  }
  if (!input.text?.trim() && !input.photoUri) {
    throw new Error('Add a note or photo to save a memory');
  }

  let photoUrl: string | null = null;
  if (input.photoUri) {
    try {
      photoUrl = await uploadMemoryPhoto(tripId, userId, input.photoUri);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to upload selected photo';
      throw new Error(`Photo upload failed: ${message}`);
    }
  }

  try {
    await addDoc(collection(db, 'trips', tripId, 'memories'), {
      text: input.text?.trim() ?? '',
      photoUrl,
      visibility: input.visibility ?? 'collaborators',
      createdBy: userId,
      createdAt: serverTimestamp(),
    });
  } catch (error) {
    const code = (error as { code?: string } | null)?.code ?? '';
    if (code.includes('permission')) {
      throw new Error('Memory record was blocked by permissions. Make sure you still have access to this trip.');
    }
    const message = error instanceof Error ? error.message : 'Unable to save memory details';
    throw new Error(`Memory record failed: ${message}`);
  }
};

export const fetchTripMemoryPreview = async (tripId: string): Promise<TripMemoryPreview | null> => {
  if (!tripId) {
    return null;
  }

  const memoriesRef = collection(db, 'trips', tripId, 'memories');
  const previewQuery = query(memoriesRef, orderBy('createdAt', 'desc'), limit(1));
  const [snapshot, countSnap] = await Promise.all([
    getDocs(previewQuery),
    getCountFromServer(memoriesRef),
  ]);

  const count = countSnap.data().count ?? 0;
  if (count === 0) {
    return { count: 0 };
  }

  const docSnapshot = snapshot.docs[0];
  const data = docSnapshot?.data();
  return {
    count,
    latestSnippet: sanitizeSnippet(data?.text),
    photoUrl: data?.photoUrl ?? null,
    lastContributor: data?.createdBy,
  };
};

/**
 * Fetch all trip memories
 */
export const fetchTripMemories = async (
  tripId: string,
  options?: {
    limitCount?: number;
    cursorCreatedAt?: Date;
    newestFirst?: boolean;
  },
): Promise<TripMemory[]> => {
  if (!tripId) {
    return [];
  }

  try {
    const memoriesRef = collection(db, 'trips', tripId, 'memories');
    const limitCount = Math.max(1, Math.min(300, options?.limitCount ?? 120));
    const constraints: QueryConstraint[] = [orderBy('createdAt', 'desc')];
    if (options?.cursorCreatedAt instanceof Date) {
      constraints.push(startAfter(Timestamp.fromDate(options.cursorCreatedAt)));
    }
    constraints.push(limit(limitCount));
    const memoriesQuery = query(memoriesRef, ...constraints);
    const snapshot = await getDocs(memoriesQuery);

    const memories = snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        tripId,
        createdBy: data.createdBy,
        text: data.text || '',
        photoUrl: data.photoUrl || null,
        visibility: (data.visibility || 'collaborators') as TripMemoryVisibility,
        createdAt: data.createdAt?.toDate?.() || new Date(data.createdAt),
      };
    });
    if (options?.newestFirst === true) {
      return memories;
    }
    return memories.reverse();
  } catch (error) {
    console.error('[TripMemories] Error fetching memories:', error);
    return [];
  }
};

/**
 * Get all photos from trip memories
 */
export const getTripMemoryPhotos = async (tripId: string): Promise<string[]> => {
  const memories = await fetchTripMemories(tripId);
  return memories
    .map(memory => memory.photoUrl)
    .filter((photoUrl): photoUrl is string => !!photoUrl);
};

